import React from 'react';
import { Scan, MapPin, Calendar, Package, ArrowRight } from 'lucide-react';
import type { KitComponent } from '@/app/components/KittingWorkspace';

interface KittingCurrentPromptProps {
  component: KitComponent;
}

export default function KittingCurrentPrompt({ component }: KittingCurrentPromptProps) {
  const remaining = component.requiredQty - component.scannedQty;

  return (
    <div className="card-elevated border-primary/30 bg-primary/5 p-4 relative overflow-hidden">
      {/* Accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-lg" />

      <div className="pl-3">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center flex-shrink-0 pulse-ring">
            <Scan size={13} className="text-primary" />
          </div>
          <p className="text-xs font-semibold text-primary uppercase tracking-wider">
            Scan Next Item
          </p>
        </div>

        <h3 className="text-base font-bold text-foreground mb-1 leading-tight">
          {component.itemName}
        </h3>
        <p className="text-xs font-mono-data text-muted-foreground mb-3">
          {component.itemCode}
        </p>

        {/* Barcode display */}
        <div className="bg-muted rounded-lg px-3 py-2 mb-3">
          <p className="text-2xs text-muted-foreground mb-0.5">Expected Barcode</p>
          <p className="text-xs font-mono-data text-foreground break-all leading-relaxed">
            {component.barcode}
          </p>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-muted/50 rounded-lg p-2">
            <p className="text-2xs text-muted-foreground mb-0.5 flex items-center gap-1">
              <Package size={9} /> Batch (FEFO)
            </p>
            <p className="text-xs font-mono-data text-foreground font-semibold">
              {component.batchSuggested}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <p className="text-2xs text-muted-foreground mb-0.5 flex items-center gap-1">
              <Calendar size={9} /> Expiry
            </p>
            <p className="text-xs font-mono-data text-foreground font-semibold">
              {component.expiryDate}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <p className="text-2xs text-muted-foreground mb-0.5 flex items-center gap-1">
              <MapPin size={9} /> Location
            </p>
            <p className="text-xs font-mono-data text-foreground font-semibold">
              {component.location}
            </p>
          </div>
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-2">
            <p className="text-2xs text-primary mb-0.5">Remaining</p>
            <p className="text-lg font-bold text-primary tabular-nums">
              {remaining}{' '}
              <span className="text-xs font-normal">{component.uom}</span>
            </p>
          </div>
        </div>

        {/* Qty progress */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{
                width: `${Math.round((component.scannedQty / component.requiredQty) * 100)}%`,
              }}
            />
          </div>
          <span className="text-xs font-mono-data text-muted-foreground tabular-nums whitespace-nowrap">
            {component.scannedQty}/{component.requiredQty}
          </span>
          <ArrowRight size={12} className="text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}